import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { ErrorState, Loading, PageHeader } from "@/components/ds";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PeriodPicker, PRESETS } from "@/components/finance/PeriodPicker";
import { FinanceOverview } from "@/components/finance/FinanceOverview";
import { FinanceSales } from "@/components/finance/FinanceSales";
import { FinanceReceivables } from "@/components/finance/FinanceReceivables";
import { FinanceProduction } from "@/components/finance/FinanceProduction";
import { FinancePartners } from "@/components/finance/FinancePartners";
import { FinanceAnalysis } from "@/components/finance/FinanceAnalysis";
import { EntriesPanel } from "@/components/finance/EntriesPanel";

const initialPeriod = () => {
  const [start, end] = PRESETS.mes.range();
  return { start, end, preset: "mes" };
};

export default function Finance() {
  const [period, setPeriod] = useState(initialPeriod);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    setData(null);
    api
      .get("/finance/overview", { params: { start: period.start, end: period.end } })
      .then(({ data }) => setData(data))
      .catch((e) => setError(apiError(e)));
  }, [period.start, period.end]);

  useEffect(load, [load]);

  return (
    <div className="space-y-6" data-testid="finance-page">
      <PageHeader
        title="Financeiro"
        subtitle="Visão gerencial do negócio: quanto você vendeu, quanto já entrou, quanto falta entrar, quanto gastou e o que está dando lucro. Vender não é receber — só o pagamento confirmado entra em Recebido."
      />

      <PeriodPicker value={period} onChange={setPeriod} />

      <Tabs defaultValue="visao" className="w-full">
        <TabsList className="flex h-auto w-full flex-wrap gap-1 bg-secondary/60">
          <TabsTrigger value="visao" data-testid="tab-overview">
            Visão geral
          </TabsTrigger>
          <TabsTrigger value="vendas" data-testid="tab-sales">
            Vendas
          </TabsTrigger>
          <TabsTrigger value="recebimentos" data-testid="tab-receivables">
            Recebimentos
          </TabsTrigger>
          <TabsTrigger value="despesas" data-testid="tab-expenses">
            Despesas
          </TabsTrigger>
          <TabsTrigger value="producao" data-testid="tab-production">
            Produção
          </TabsTrigger>
          <TabsTrigger value="parceiros" data-testid="tab-partners">
            Parceiros
          </TabsTrigger>
          <TabsTrigger value="analises" data-testid="tab-analysis">
            Análises
          </TabsTrigger>
        </TabsList>

        <TabsContent value="visao" className="mt-6">
          {error ? (
            <ErrorState message={error} onRetry={load} />
          ) : !data ? (
            <Loading label="Calculando seus números..." />
          ) : (
            <FinanceOverview data={data} />
          )}
        </TabsContent>

        <TabsContent value="vendas" className="mt-6">
          <FinanceSales period={period} />
        </TabsContent>

        <TabsContent value="recebimentos" className="mt-6">
          <FinanceReceivables />
        </TabsContent>

        <TabsContent value="despesas" className="mt-6">
          <EntriesPanel />
        </TabsContent>

        <TabsContent value="producao" className="mt-6">
          {data ? <FinanceProduction data={data} /> : <Loading label="Carregando produção..." />}
        </TabsContent>

        <TabsContent value="parceiros" className="mt-6">
          {data ? <FinancePartners data={data} /> : <Loading label="Carregando parceiros..." />}
        </TabsContent>

        <TabsContent value="analises" className="mt-6">
          {data ? <FinanceAnalysis data={data} /> : <Loading label="Carregando análises..." />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
